// Parts shared by the dashboard and Reports, so the two can't drift apart
// (docs/plans/reports-dashboard-style.md).
import { Badge } from '@ims/ui'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

/** A chart card's head: the big figure, a pill for how it stands, and one line of words. */
export function ChartHead({
  total,
  pill,
  tone = 'success',
  children,
}: {
  total: ReactNode
  pill?: ReactNode
  tone?: 'success' | 'neutral' | 'warning' | 'danger'
  children?: ReactNode
}) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-bold tracking-tight text-ink">{total}</span>
        {pill && <Badge tone={tone}>{pill}</Badge>}
      </div>
      {children && <p className="mt-1 text-sm text-muted">{children}</p>}
    </>
  )
}

/** "↑ 12% more than Asoj", or nothing when there is no fair comparison. */
export function changeWords(percent: number | null, name: string): string | null {
  if (percent === null) return null
  if (percent > 0) return `↑ ${percent}% more than ${name}`
  if (percent < 0) return `↓ ${-percent}% less than ${name}`
  return `The same as ${name}`
}

export interface RankRow {
  key: string
  name: string
  to?: string
  /** A short extra beside the name: "86 bills". */
  note?: string
  amount: ReactNode
  /** What the bar measures; the first row's is the longest. */
  value: number
}

/** A ranked list: name and amount, with a thin bar under them (the dashboard's Top sellers). */
export function RankRows({ rows, numbered }: { rows: readonly RankRow[]; numbered?: boolean }) {
  const top = Math.max(0, ...rows.map((r) => r.value))
  return (
    <ol className="divide-y divide-border">
      {rows.map((r, i) => (
        <li key={r.key} className="px-5 py-2.5 text-sm">
          <div className="flex justify-between gap-3">
            <span className="min-w-0">
              {numbered && <span className="tabular mr-2 text-subtle">{i + 1}</span>}
              {r.to ? (
                <Link to={r.to} className="font-medium hover:text-primary hover:underline">
                  {r.name}
                </Link>
              ) : (
                <span className="font-medium">{r.name}</span>
              )}
              {r.note && <span className="ml-2 text-xs text-muted">{r.note}</span>}
            </span>
            <span className="tabular shrink-0">{r.amount}</span>
          </div>
          <div
            aria-hidden
            className="mt-1.5 h-1 rounded-full bg-sell/70"
            style={{ width: `${top > 0 ? Math.max(4, (Math.max(0, r.value) / top) * 100) : 4}%` }}
          />
        </li>
      ))}
    </ol>
  )
}
